import React, { useEffect, useRef, useState } from "react"
import { Input, Popconfirm } from "antd"
import ReactMarkdown, { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPaperPlane, faRotateLeft, faXmark } from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  AssistantMessage,
  messagesToSend,
  selectConversation,
  useAssistantStore,
} from "../assistantStore"
import { useAssistantScreen } from "../screenRegistry"
import { turnErrorText, useAssistantTurn } from "../useAssistant"
import type { AssistantTurnResponse } from "../types"
import ProposalCard from "./ProposalCard.component"
import AssistantWorking, { WORKING_WORDS } from "./AssistantWorking.component"

export const WORKING_TEXT = WORKING_WORDS[0]
export const CLEAR_CONFIRM_TEXT = "Clear this conversation? Its proposals go too."
export const NO_SCREEN_TEXT = "The assistant is not available on this page."

const PLACEHOLDER = "Ask the assistant (Enter sends, Shift+Enter for a new line)"

/**
 * Links in a reply open in a new tab, with no access back to the app. An
 * image is never loaded: it shows as a link with its alt text (or its
 * address), opened only on a click.
 */
const MARKDOWN_COMPONENTS: Components = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
  img: ({ src, alt }) => {
    const address = typeof src === "string" ? src : ""
    const text = alt || address
    if (!address) return <>{text}</>
    return (
      <a href={address} target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    )
  },
}

/** One message: the user's on the right, the assistant's reply (markdown) on the left. */
const MessageBubble: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  if (message.role === "user") {
    return (
      <div className="self-end max-w-[85%] rounded bg-gray-100 px-2 py-1 text-sm whitespace-pre-wrap">
        {message.text}
      </div>
    )
  }
  if (message.failed) {
    return (
      <div className="text-sm text-red-600" role="alert">
        {message.text}
      </div>
    )
  }
  return (
    // Replies often carry a table (from run_table): the arbitrary variants
    // give it rules and spacing without a stylesheet.
    <div className="text-sm overflow-x-auto [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:pl-4 [&_ul]:list-disc [&_ol]:list-decimal [&_table]:my-1 [&_table]:border-collapse [&_table]:text-xs [&_th]:border [&_th]:border-gray-300 [&_th]:px-1 [&_th]:bg-gray-50 [&_td]:border [&_td]:border-gray-300 [&_td]:px-1">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {message.text}
      </ReactMarkdown>
    </div>
  )
}

/**
 * The assistant's chat for the current project: the conversation with the
 * proposal cards under the reply that brought them, a "Working…" line while
 * a turn is on its way, and the question box (Enter sends, Shift+Enter is a
 * new line). Each turn sends the open screen's context, read at that moment
 * from its registration. Nothing here saves anything.
 */
const AssistantPanel: React.FC = () => {
  const { projectId, owner, registration } = useAssistantScreen()
  const conversation = useAssistantStore(selectConversation(projectId))
  const setOpen = useAssistantStore((s) => s.setOpen)
  const clear = useAssistantStore((s) => s.clear)
  const { sendTurn } = useAssistantTurn()
  const [draft, setDraft] = useState("")
  const listRef = useRef<HTMLDivElement>(null)
  const { messages, proposals, busy, pending } = conversation

  useEffect(() => {
    const list = listRef.current
    if (list) list.scrollTop = list.scrollHeight
  }, [messages.length, busy])

  const canSend = !busy && registration !== null && draft.trim() !== ""

  const send = async (): Promise<void> => {
    const question = draft.trim()
    if (!canSend || !registration) return
    const store = useAssistantStore.getState()
    const sent = messagesToSend(selectConversation(projectId)(store).messages, question)
    store.startTurn(projectId, question)
    setDraft("")
    // sendTurn resolves every failure to a response; the catch is for a
    // screen whose context cannot be read, so the turn never stays busy.
    let res: AssistantTurnResponse
    try {
      res = await sendTurn({
        projectId,
        owner,
        screen: registration.screen,
        messages: sent,
        context: registration.getContext(),
      })
    } catch {
      res = { success: false, code: "error" }
    }
    const after = useAssistantStore.getState()
    if (res.success && res.data) {
      after.addTurn(projectId, question, res.data.reply, res.data.proposals)
    } else {
      after.addFailedTurn(projectId, question, turnErrorText(res))
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void send()
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0" aria-label="Assistant">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
        <h3 className="m-0 text-sm font-semibold">Assistant</h3>
        <div className="flex items-center gap-1">
          <Popconfirm
            title={CLEAR_CONFIRM_TEXT}
            okText="Clear"
            cancelText="Keep"
            onConfirm={() => clear(projectId)}
            disabled={busy || messages.length === 0}
          >
            <DataQButton
              type="text"
              size="small"
              aria-label="Clear conversation"
              isDisabled={busy || messages.length === 0}
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </DataQButton>
          </Popconfirm>
          <DataQButton
            type="text"
            size="small"
            aria-label="Close assistant"
            onClick={() => setOpen(projectId, false)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </DataQButton>
        </div>
      </div>
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-2 flex flex-col gap-2 [&>*]:shrink-0"
        aria-live="polite"
      >
        {messages.length === 0 && !busy && (
          <div className="text-xs text-gray-500">
            Ask a question about this project, or ask for changes. Nothing is
            saved until you save it yourself.
          </div>
        )}
        {messages.map((m) => (
          <React.Fragment key={m.id}>
            <MessageBubble message={m} />
            {proposals
              .filter((p) => p.messageId === m.id)
              .map((p) => (
                <ProposalCard key={p.proposal.id} stored={p} />
              ))}
          </React.Fragment>
        ))}
        {busy && pending !== null && (
          <MessageBubble message={{ id: 0, role: "user", text: pending }} />
        )}
        {busy && <AssistantWorking />}
      </div>
      <div className="border-t border-gray-200 p-2 flex flex-col gap-2">
        {registration === null && (
          <div className="text-xs text-gray-500">{NO_SCREEN_TEXT}</div>
        )}
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={PLACEHOLDER}
          aria-label="Message to the assistant"
          autoSize={{ minRows: 2, maxRows: 6 }}
          disabled={registration === null}
        />
        <div className="flex justify-end">
          <DataQButton type="primary" isDisabled={!canSend} onClick={() => void send()}>
            <FontAwesomeIcon icon={faPaperPlane} />
            Send
          </DataQButton>
        </div>
      </div>
    </div>
  )
}

export default AssistantPanel
